import { Injectable } from '@angular/core';
import { RagChunk, RagDocument } from '../models/rag.models';

export interface Keyword {
  term: string;
  score: number;
  frequency: number; // total occurrences across all chunks
  documentFrequency: number; // number of documents containing the term
}

@Injectable({ providedIn: 'root' })
export class KeywordExtractionService {
  /**
   * Tokenize text using same logic as BM25Service.
   */
  private tokenize(text: string): string[] {
    return text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .split(/\s+/)
      .filter(Boolean);
  }

  /**
   * Alias for {@link extractKeywords}. Exposed to allow components to
   * retrieve keyword terms directly without needing to map the full
   * {@link Keyword} objects. The default limit matches the original
   * implementation used in the chat component.
   */
  searchKeywords(chunks: RagChunk[], limit = 50): Keyword[] {
    return this.extractKeywords(chunks, limit);
  }

  /**
   * Convenience helper that extracts keyword terms directly from a list of
   * {@link RagDocument}s. It aggregates all indexed chunks and returns the
   * top keyword terms.
   */
  /**
   * Return keyword terms from a list of documents.
   * @param documents List of {@link RagDocument} to analyze.
   * @param limit Number of terms to return. Ignored when `randomPick` is true.
   * @param randomPick If true, randomly selects up to `limit` terms from the full set
   *                   instead of returning the top scored ones.
   */
  getKeywordTermsFromDocuments(documents: RagDocument[], limit = 10, randomPick = false): string[] {
    const allChunks: RagChunk[] = [];
    for (const doc of documents) {
      if (doc.status === 'indexed' && doc.chunks.length > 0) {
        allChunks.push(...doc.chunks);
      }
    }
    const keywords = this.extractKeywords(allChunks, randomPick ? undefined : limit);
    const terms = keywords.map(k => k.term);
    if (randomPick) {
      // Shuffle and take up to `limit` terms
      for (let i = terms.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [terms[i], terms[j]] = [terms[j], terms[i]];
      }
      return terms.slice(0, limit);
    }
    return terms;
  }

  /**
   * Extract top keywords/phrases from document chunks.
   * @param chunks Array of RagChunk to analyze.
   * @param limit Number of top terms to return (default: 50).
   */
  extractKeywords(chunks: RagChunk[], limit = 50): Keyword[] {
    const termTF = new Map<string, number>();
    const termDF = new Map<string, number>();
    const bigramTF = new Map<string, number>();
    const bigramDF = new Map<string, number>();

    const stopWords = new Set([
      'the', 'of', 'and', 'to', 'in', 'a', 'for', 'is', 'are', 'be', 'as',
      'by', 'which', 'that', 'than', 'such', 'this', 'from', 'at', 'into',
      'use', 'an', 'may', 'shall', 'note', 'materials', 'exchange', 'process',
      'gas', 'nuclear', 'equipment', 'separation', 'agency', 'systems',
      'prepared', 'uranium', 'designed', 'with', 'especially', '2', '1', '3',
      '4', '5', '6', 'but', 'nor', 'so', 'yet', 'if', 'when', 'where', 'why',
      'how', 'what', 'which', 'who', 'whom', 'whose', 'that', 'these', 'those',
      'all', 'any', 'both', 'each', 'few', 'more', 'most', 'other', 'some',
      'no', 'own', 'same', 'too', 'very', 'just', 'now'
    ]);

    const N = chunks.length;

    for (const chunk of chunks) {
      const tokens = this.tokenize(chunk.content);
      const uniqueTermsInDoc = new Set<string>();
      // Unigrams
      for (const token of tokens) {
        termTF.set(token, (termTF.get(token) ?? 0) + 1);
        if (!uniqueTermsInDoc.has(token)) {
          termDF.set(token, (termDF.get(token) ?? 0) + 1);
          uniqueTermsInDoc.add(token);
        }
      }

      // Bigrams
      const uniqueBigramsInDoc = new Set<string>();
      for (let i = 0; i < tokens.length - 1; i++) {
        const bigram = `${tokens[i]} ${tokens[i + 1]}`;
        bigramTF.set(bigram, (bigramTF.get(bigram) ?? 0) + 1);
        if (!uniqueBigramsInDoc.has(bigram)) {
          bigramDF.set(bigram, (bigramDF.get(bigram) ?? 0) + 1);
          uniqueBigramsInDoc.add(bigram);
        }
      }
    }

    const keywords: Keyword[] = [];

    // Helper to compute score for a term or bigram
    const addKeyword = (term: string, tf: number, df: number) => {
      // With a single chunk, document-frequency filtering (and IDF) is
      // meaningless, so fall back to term-frequency scoring so short
      // documents still surface keywords.
      if (N > 1 && (df / N > 0.7 || df < 2)) return; // filter common/rare terms
      const idf = Math.log(1 + (N - df + 0.5) / (df + 0.5));
      const score = Math.log(1 + tf) * idf;
      keywords.push({ term, score, frequency: tf, documentFrequency: df });
    };

    // Process unigrams
    for (const [term, tf] of termTF.entries()) {
      if (stopWords.has(term)) continue;
      if (term.length < 3) continue;
      const df = termDF.get(term)!;
      addKeyword(term, tf, df);
    }

    // Process bigrams
    for (const [bigram, tf] of bigramTF.entries()) {
      const parts = bigram.split(' ');
      if (parts.length !== 2) continue;
      const [w1, w2] = parts;
      if (stopWords.has(w1) || stopWords.has(w2)) continue;
      if (w1.length < 3 || w2.length < 3) continue;
      const df = bigramDF.get(bigram)!;
      addKeyword(bigram, tf, df);
    }

    // Sort by score descending
    keywords.sort((a, b) => b.score - a.score);

    // Deduplicate overlapping phrases: keep longer ones when one contains another
    const finalKeywords: Keyword[] = [];
    for (const kw of keywords) {
      let skip = false;
      for (const existing of finalKeywords) {
        if (existing.term.includes(kw.term)) {
          // Existing longer phrase already present; skip this shorter one
          skip = true;
          break;
        }
        if (kw.term.includes(existing.term)) {
          // Current term contains an existing shorter term; replace it
          const idx = finalKeywords.indexOf(existing);
          finalKeywords.splice(idx, 1, kw);
          skip = true;
          break;
        }
      }
      if (!skip) {
        finalKeywords.push(kw);
      }
    }

    return finalKeywords.slice(0, limit);
  }
}
